import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, Table, Tag, Skeleton, Alert } from 'antd';
import {
  TeamOutlined, ArrowRightOutlined, SwapRightOutlined, BankOutlined,
  CalendarOutlined, CheckCircleOutlined, WarningOutlined, FileTextOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import bilateralesService from '../../../services/bilateralesService';
import { formatDate } from '../../../variables/constants';
import KpiCards from '../../../components/KpiCards';
import { useTranslation, Trans } from 'react-i18next';
import { localeIntl } from '../../../i18n/format';

const fmt = (v) => v != null ? new Intl.NumberFormat(localeIntl()).format(v) + ' FCFA' : '-';
const sommeNombre = (items, champ) => items.reduce((s, r) => s + (Number(r[champ]) || 0), 0);

/**
 * Onglet "Échanges bilatéraux" de DetailsSinistre - affiche toutes les
 * occurrences de ce sinistre dans des rencontres bilatérales avec
 * décisions de séance et montants négociés.
 */
export default function OngletBilateraux({ sinistreTrackingId }) {
  const { t } = useTranslation('dossierSinistre');
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!sinistreTrackingId) return;
    bilateralesService.getDossiersBilaterauxDuSinistre(sinistreTrackingId)
      .then(setItems)
      .catch(() => setItems([]));
  }, [sinistreTrackingId]);

  if (items === null) return <Skeleton active style={{ padding: 24 }} />;
  if (!items.length) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description={t('ongletBilateraux.aucun')} />
      </div>
    );
  }

  const columns = [
    {
      title: t('ongletBilateraux.colonnes.rencontre'),
      dataIndex: 'rbReference',
      render: (v, r) => (
        <button onClick={() => navigate(`/bilaterales/${r.rbTrackingId}`)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, color: '#1a6b3c' }}>
          {v} <ArrowRightOutlined style={{ fontSize: 11 }} />
        </button>
      ),
    },
    {
      title: t('ongletBilateraux.colonnes.dateSeance'), dataIndex: 'dateSession',
      render: (v) => <span><CalendarOutlined style={{ color: '#9ca3af', marginRight: 6 }} />{formatDate(v)}</span>,
    },
    {
      title: t('ongletBilateraux.colonnes.compagnie'), dataIndex: 'compagnieRaisonSociale',
      render: (v) => v ? <span><BankOutlined style={{ color: '#9ca3af', marginRight: 6 }} />{v}</span> : '-',
    },
    { title: t('ongletBilateraux.colonnes.montantReclame'), dataIndex: 'montantReclame', align: 'right', render: fmt },
    { title: t('ongletBilateraux.colonnes.bapNegocie'), dataIndex: 'montantBap', align: 'right', render: fmt },
    {
      title: t('ongletBilateraux.colonnes.bapTotal'), dataIndex: 'bapTotal', align: 'right',
      render: (v) => <strong style={{ color: '#1a6b3c' }}>{fmt(v)}</strong>,
    },
    {
      title: t('ongletBilateraux.colonnes.statutSeance'),
      dataIndex: 'nouveauStatutSinistre',
      render: (v, r) => v
        ? <Tag color="green" icon={<SwapRightOutlined />}>{v}</Tag>
        : (r.statutSinistreSnapshot ? <Tag>{r.statutSinistreSnapshot}</Tag> : '-'),
    },
    {
      title: t('ongletBilateraux.colonnes.datePromise'),
      dataIndex: 'datePromesseAssureur',
      render: (v) => v
        ? <Tag color="gold" icon={<ClockCircleOutlined />}>{formatDate(v)}</Tag>
        : '-',
    },
  ];

  const nbChangements = items.filter(r => r.nouveauStatutSinistre).length;

  return (
    <div style={{ padding: 24 }}>
      <KpiCards
        items={[
          { label: t('ongletBilateraux.kpi.rencontres'), value: items.length, icon: <TeamOutlined />, color: '#1a6b3c', bg: '#f0fdf4', suffix: t('ongletBilateraux.kpi.seances', { count: items.length }) },
          { label: t('ongletBilateraux.kpi.montantReclame'), value: fmt(sommeNombre(items, 'montantReclame')), icon: <FileTextOutlined />, color: '#0891b2', bg: '#ecfeff' },
          { label: t('ongletBilateraux.kpi.bapNegocie'), value: fmt(sommeNombre(items, 'montantBap')), icon: <BankOutlined />, color: '#059669', bg: '#ecfdf5' },
          { label: t('ongletBilateraux.kpi.changementsStatut'), value: nbChangements, icon: <SwapRightOutlined />, color: '#2563eb', bg: '#eff6ff' },
        ]}
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 10 }}
        message={<Trans t={t} i18nKey="ongletBilateraux.examine" count={items.length} components={{ strong: <strong /> }} />}
      />

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          rowKey="dbTrackingId"
          size="small"
          dataSource={items}
          columns={columns}
          pagination={false}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {r.decisionSeance && (
                  <div>
                    <CheckCircleOutlined style={{ color: '#1a6b3c', marginRight: 8 }} />
                    <strong>{t('ongletBilateraux.decision')}</strong> {r.decisionSeance}
                  </div>
                )}
                {r.blocageIdentifie && (
                  <div style={{ color: '#c47800' }}>
                    <WarningOutlined style={{ marginRight: 8 }} />
                    <strong>{t('ongletBilateraux.blocage')}</strong> {r.blocageIdentifie}
                  </div>
                )}
                {r.observations && (
                  <div style={{ color: '#6b7280' }}>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    {r.observations}
                  </div>
                )}
                {!r.decisionSeance && !r.blocageIdentifie && !r.observations && (
                  <span style={{ color: '#9ca3af' }}>{t('ongletBilateraux.aucunDetail')}</span>
                )}
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
}
